import { createSlice } from '@reduxjs/toolkit';
import { DynamoConnector } from '../../DynamoConnector';

export const DEFAULT_BIN_KEY = 'bracket_4';

// Bracket / power-level bin picker options, grouped for Spectrum Picker sections.
export const BIN_SECTIONS = [
    {
        id: 'brackets',
        name: 'Bracket',
        children: [
            { key: 'bracket_1', label: 'Bracket 1' },
            { key: 'bracket_2', label: 'Bracket 2' },
            { key: 'bracket_3', label: 'Bracket 3' },
            { key: 'bracket_4', label: 'Bracket 4' },
            { key: 'bracket_5', label: 'Bracket 5' },
        ],
    },
    {
        id: 'power',
        name: 'Power Level',
        children: [
            { key: 'power_1_3', label: 'Power 1–3' },
            { key: 'power_3_5', label: 'Power 3–5' },
            { key: 'power_5_7', label: 'Power 5–7' },
            { key: 'power_7_9', label: 'Power 7–9' },
            { key: 'power_9_10', label: 'Power 9–10' },
        ],
    },
];

export const BIN_LABEL_BY_KEY = BIN_SECTIONS.reduce((acc, section) => {
    section.children.forEach((child) => {
        acc[child.key] = child.label;
    });
    return acc;
}, {});

// Archetype filter is part of the cache key for a bin response, since the
// server returns different aggregates for ANY vs MIDRANGE vs MIDRANGE/COMBAT
// at the same bin. Encoded as `${binKey}|${archetype||''}|${sub||''}` so
// the ANY/ANY case collapses back to plain `${binKey}|` and stays a
// stable cache hit alongside non-archetype callers.
export const buildBinCacheKey = (binKey, archetype, subArchetype) => {
    const major = archetype && archetype !== 'ANY' ? archetype : '';
    const sub = subArchetype && subArchetype !== 'ANY' ? subArchetype : '';
    return `${binKey || ''}|${major}|${sub}`;
};

export const commanderCardStatsSlice = createSlice({
    name: 'commanderCardStats',
    initialState: {
        commanderIndexId: null,
        selectedBinKey: DEFAULT_BIN_KEY,
        // Archetype filter currently selected by the UI. null means
        // ANY/ANY (no filter).
        selectedArchetype: null,
        selectedSubArchetype: null,
        // Cache keyed by buildBinCacheKey(binKey, archetype, sub) so
        // switching between ANY and a specific archetype doesn't clobber
        // each other's data.
        responsesByBin: {},
        fetchingBins: {},
        isDisabled: false,
        // Health probe (mode=health) — tells the UI which bins have data
        // and whether the commander has been aggregated at all. Populated
        // lazily via fetchCommanderStatsHealth; consumers should treat
        // `healthById` as the source of truth for bin enable/disable
        // decisions.
        healthById: {}, // { [commanderIndexId]: { status, eligible, deckCount, threshold, availableBins, lastSuccessfulAggregationAt, healthReadStatus } }
        healthFetching: {}, // { [commanderIndexId]: true }
    },
    reducers: {
        resetCommanderCardStats: (state, action) => {
            state.commanderIndexId = action.payload?.id ?? null;
            state.selectedBinKey = action.payload?.selectedBinKey ?? DEFAULT_BIN_KEY;
            state.selectedArchetype = action.payload?.selectedArchetype ?? null;
            state.selectedSubArchetype = action.payload?.selectedSubArchetype ?? null;
            state.responsesByBin = {};
            state.fetchingBins = {};
            state.isDisabled = false;
        },
        setSelectedBinKey: (state, action) => {
            state.selectedBinKey = action.payload;
        },
        setSelectedArchetype: (state, action) => {
            const { archetype, subArchetype } = action.payload || {};
            state.selectedArchetype = archetype || null;
            state.selectedSubArchetype = subArchetype || null;
        },
        setBinFetching: (state, action) => {
            const { cacheKey, isFetching } = action.payload;
            if (isFetching) {
                state.fetchingBins[cacheKey] = true;
            } else {
                delete state.fetchingBins[cacheKey];
            }
        },
        setBinResponse: (state, action) => {
            const { cacheKey, response } = action.payload;
            state.responsesByBin[cacheKey] = response;
            delete state.fetchingBins[cacheKey];
            state.isDisabled = false;
        },
        setCommanderCardStatsDisabled: (state) => {
            state.responsesByBin = {};
            state.fetchingBins = {};
            state.isDisabled = true;
        },
        setHealthFetching: (state, action) => {
            const { id, isFetching } = action.payload || {};
            if (!id) return;
            if (isFetching) {
                state.healthFetching[id] = true;
            } else {
                delete state.healthFetching[id];
            }
        },
        setHealthResponse: (state, action) => {
            const { id, data } = action.payload || {};
            if (!id) return;
            state.healthById[id] = data || null;
            delete state.healthFetching[id];
        },
    },
});

export const {
    resetCommanderCardStats,
    setSelectedBinKey,
    setSelectedArchetype,
    setBinFetching,
    setBinResponse,
    setCommanderCardStatsDisabled,
    setHealthFetching,
    setHealthResponse,
} = commanderCardStatsSlice.actions;

const normalizeArchetype = (archetype) => (archetype && archetype !== 'ANY' ? archetype : null);
const normalizeSub = (sub) => (sub && sub !== 'ANY' ? sub : null);

export const fetchCommanderCardStatsForBin =
    (id, binKey, archetype = null, subArchetype = null) =>
    (dispatch, getState) => {
        if (!id || !binKey) return;
        const major = normalizeArchetype(archetype);
        const sub = normalizeSub(subArchetype);

        const state = getState().commanderCardStats;

        if (state.commanderIndexId !== id) {
            dispatch(
                resetCommanderCardStats({
                    id,
                    selectedBinKey: binKey,
                    selectedArchetype: major,
                    selectedSubArchetype: sub,
                })
            );
        }

        const cacheKey = buildBinCacheKey(binKey, major, sub);
        const latest = getState().commanderCardStats;
        if (latest.isDisabled) return;
        if (latest.responsesByBin[cacheKey]) return;
        if (latest.fetchingBins[cacheKey]) return;

        dispatch(setBinFetching({ cacheKey, isFetching: true }));

        DynamoConnector.getCommanderCardStats(
            id,
            binKey,
            ({ disabled, data }) => {
                if (getState().commanderCardStats.commanderIndexId !== id) return;

                if (disabled) {
                    dispatch(setCommanderCardStatsDisabled());
                    return;
                }

                dispatch(setBinResponse({ cacheKey, response: data }));
            },
            { archetype: major, subArchetype: sub }
        );
    };

export const selectCommanderBin =
    (id, binKey, archetype = null, subArchetype = null) =>
    (dispatch) => {
        dispatch(setSelectedBinKey(binKey));
        dispatch(setSelectedArchetype({ archetype, subArchetype }));
        dispatch(fetchCommanderCardStatsForBin(id, binKey, archetype, subArchetype));
    };

export const fetchCommanderStatsHealth =
    (id, { force = false } = {}) =>
    (dispatch, getState) => {
        if (!id) return;
        const state = getState().commanderCardStats;
        if (!force && state.healthById[id]) return;
        if (state.healthFetching[id]) return;

        dispatch(setHealthFetching({ id, isFetching: true }));

        DynamoConnector.getCommanderStatsHealth(id, ({ disabled, data }) => {
            if (disabled) {
                dispatch(setHealthResponse({ id, data: { status: 'disabled', availableBins: {} } }));
                return;
            }
            dispatch(setHealthResponse({ id, data }));
        });
    };

export default commanderCardStatsSlice.reducer;
